import React, { useState, useEffect, useCallback } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Comments from '../components/Comments';
import '../css.css';

// 동적 색상 그래프 컴포넌트 - 표 전체 기준으로 그래프 생성
const ColorGraph = ({ value, groupData, columns }) => {
    const isDarkMode = document.body.classList.contains('dark-mode');
    
    // 값이 null이나 undefined면 표시하지 않음
    if (value === null || value === undefined) {
        return (
            <div style={{ 
                textAlign: 'center', 
                padding: '8px',
                fontSize: '11px',
                color: isDarkMode ? '#999' : '#999'
            }}>
                -
            </div>
        );
    }

    // 숫자로 변환
    const numValue = Number(value);
    
    // 값이 0이면 0을 표시하되 그래프는 표시하지 않음
    if (numValue === 0) {
        return (
            <div style={{ 
                textAlign: 'center', 
                padding: '8px',
                fontSize: '11px',
                color: isDarkMode ? '#999' : '#999'
            }}>
                0
            </div>
        );
    }

    // 표 전체에서 모든 유효한 값들을 추출 (null, undefined, 0 제외)
    const allValues = [];
    groupData.forEach(item => {
        columns.forEach(col => {
            const val = item[col];
            if (val !== null && val !== undefined && val !== 0 && !isNaN(val)) {
                allValues.push(Number(val));
            }
        });
    });

    // 유효한 값이 1개 이하면 그래프 없이 값만 표시
    if (allValues.length <= 1) {
        return (
            <div style={{ 
                textAlign: 'center', 
                padding: '8px',
                fontWeight: 'bold'
            }}>
                {numValue}
            </div>
        );
    }

    // 최소값, 최대값 계산
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);

    // 10%-90% 범위로 정규화
    const minPercentage = 10;
    const maxPercentage = 90;
    
    let percentage;
    let normalizedValue;
    
    // 모든 값이 동일한 경우 40%에 표시
    if (minValue === maxValue) {
        percentage = 40;
        normalizedValue = 0.5; // 중간값으로 설정
    } else {
        normalizedValue = (numValue - minValue) / (maxValue - minValue);
        percentage = Math.max(minPercentage, Math.min(maxPercentage, 
            minPercentage + (normalizedValue * (maxPercentage - minPercentage))));
    }

    // 색상 계산: 낮은 값(녹색) → 높은 값(빨간색)
    const getColor = (normalizedValue) => {
        if (normalizedValue <= 0.5) {
            // 녹색에서 노란색으로 (0 ~ 0.5)
            const ratio = normalizedValue / 0.5;
            if (isDarkMode) {
                // 다크모드에서는 더 어두운 색상 사용
                const red = Math.round(180 * ratio);
                const green = 180;
                return `rgb(${red}, ${green}, 0)`;
            } else {
                const red = Math.round(255 * ratio);
                const green = 255;
                return `rgb(${red}, ${green}, 0)`;
            }
        } else {
            // 노란색에서 빨간색으로 (0.5 ~ 1)
            const ratio = (normalizedValue - 0.5) / 0.5;
            if (isDarkMode) {
                // 다크모드에서는 더 어두운 색상 사용
                const red = 180;
                const green = Math.round(180 * (1 - ratio));
                return `rgb(${red}, ${green}, 0)`;
            } else {
                const red = 255;
                const green = Math.round(255 * (1 - ratio));
                return `rgb(${red}, ${green}, 0)`;
            }
        }
    };

    const barColor = getColor(normalizedValue);
    
    return (
        <div style={{ position: 'relative', width: '100%', minWidth: '80px' }}>
            {/* 배경 바 */}
            <div style={{
                width: '100%',
                height: '20px',
                backgroundColor: isDarkMode ? '#3a3a3a' : '#f0f0f0',
                border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                borderRadius: '3px',
                overflow: 'hidden',
                position: 'relative'
            }}>
                {/* 실제 값 바 */}
                <div style={{
                    width: `${percentage}%`,
                    height: '100%',
                    backgroundColor: barColor,
                    background: `linear-gradient(90deg, ${barColor}, ${barColor}dd)`,
                    transition: 'width 0.3s ease',
                    minWidth: '2px' // 최소 너비 보장
                }} />
            </div>
            
            {/* 값 텍스트 */}
            <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '10px',
                fontWeight: 'bold',
                color: isDarkMode ? '#f0f0f0' : '#333',
                textShadow: isDarkMode ? '1px 1px 1px rgba(0,0,0,0.8)' : '1px 1px 1px rgba(255,255,255,0.8)',
                pointerEvents: 'none',
                zIndex: 2
            }}>
                {numValue}
            </div>
        </div>
    );
};

const Page1 = () => {
    const [tacklexpData, setTacklexpData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const isDarkMode = document.body.classList.contains('dark-mode');

    const fetchTacklexpData = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await fetch('/api/tacklexp');
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            setTacklexpData(data);
        } catch (error) {
            console.error('Error fetching tacklexp data:', error);
            setError('태클 경험치 데이터를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTacklexpData();
    }, [fetchTacklexpData]);

    // rod_type별로 그룹화 및 컬럼 필터링
    const processedData = React.useMemo(() => {
        if (tacklexpData.length === 0) return {};

        const groups = {
            float: { name: '찌 낚시', items: [] },
            bottom: { name: '바텀 낚시', items: [] },
            spin: { name: '스핀 낚시', items: [] },
            sea: { name: '해양 낚시', items: [] }
        };

        // 데이터 그룹화
        tacklexpData.forEach(item => {
            const rodType = item.rod_type;
            if (groups[rodType]) {
                groups[rodType].items.push(item);
            }
        });

        // 각 그룹의 items를 id 순서로 정렬
        Object.keys(groups).forEach(groupKey => {
            groups[groupKey].items.sort((a, b) => a.id - b.id);
        });

        // 각 그룹별로 값이 있는 컬럼만 필터링
        const processedGroups = {};
        Object.keys(groups).forEach(groupKey => {
            const group = groups[groupKey];
            if (group.items.length === 0) return;

            // 모든 가능한 컬럼 (id, rod_type, rod 제외)
            const allColumns = [
                'tackle_fixed', 'bolognese', 'slider', 'tackle_match', 'basic_bottom',
                'pathernoster', 'in_line', 'tackle_loop', 'classic_hair', 'snowman',
                'classic_pop_up', 'method', 'pop_up', 'hinged_stiff', 'chod', 'maggot',
                'zig_rig', 'baitfish', 'carp_hair', 'carp_hairpop', 'lure', 'jerk',
                'jigging', 'three_way', 'dropshot', 'texas', 'carolina', 'wacky', 'sbirolino',
                'pilker_jig', 'marine_jig', 'fillet_jig', 'giant_sillicone_jig', 'dead_fish_jig',
                'marine_bottom', 'marine_bottom_rattle', 'marine_paternoster', 'flying_collar', 'dead_fish_bottom'
            ];

            // 이 그룹에서 값이 있는 컬럼만 필터링
            const validColumns = allColumns.filter(col => 
                group.items.some(item => item[col] !== null && item[col] !== undefined && item[col] !== 0)
            );

            if (validColumns.length > 0) {
                processedGroups[groupKey] = {
                    ...group,
                    columns: validColumns
                };
            }
        });

        return processedGroups;
    }, [tacklexpData]);

    // 컬럼명을 한글로 변환
    const getColumnName = (column) => {
        const columnNames = {
            'rod': '낚싯대',
            'tackle_fixed': '고정줄',
            'bolognese': '볼로네제',
            'slider': '슬라이더',
            'tackle_match': '매치 채비',
            'basic_bottom': '간단한 바텀',
            'pathernoster': '파터노스터',
            'in_line': '인라인',
            'tackle_loop': '루프',
            'classic_hair': '클래식 헤어',
            'snowman': '눈사람',
            'classic_pop_up': '클래식 팝업',
            'method': '메소드',
            'pop_up': '팝업 메소드',
            'hinged_stiff': '힌지드 스티프',
            'chod': '초드',
            'maggot': '구더기',
            'zig_rig': '지그 리그',
            'baitfish': '베이트피쉬',
            'carp_hair': '카프 헤어',
            'carp_hairpop': '카프 헤어팝업',
            'lure': '루어',
            'jerk': '저크베이트',
            'jigging': '지깅',
            'three_way': '쓰리웨이',
            'dropshot': '드롭샷',
            'texas': '텍사스',
            'carolina': '캐롤라니아',
            'wacky': '와키',
            'sbirolino': '스비롤리노',
            'pilker_jig': '필커 채비',
            'marine_jig': '마린 루어 지깅',
            'fillet_jig': '필렛 지깅',
            'giant_sillicone_jig': '자이언트 실리콘 지깅',
            'dead_fish_jig': '생미끼 지깅',
            'marine_bottom': '클래식 마린 바텀',
            'marine_bottom_rattle': '바텀 - 래틀 봉돌',
            'marine_paternoster': '마린 파터노스터',
            'flying_collar': '플라잉 칼러',
            'dead_fish_bottom': '바텀 생미끼'
        };
        return columnNames[column] || column;
    };

    if (loading) {
        return (
            <>
                <h1 className="page-title">태클 경험치 정보</h1>
                <div className="spinner-container">
                    <LoadingSpinner
                        height={80}
                        width={80}
                        color="#4fa94d"
                        visible={true}
                    />
                </div>
                <Comments pageName="tacklexp" />
            </>
        );
    }

    if (error) {
        return (
            <>
                <h1 className="page-title">태클 경험치 정보</h1>
                <div className="error-message">
                    <p>{error}</p>
                    <button onClick={fetchTacklexpData}>다시 시도</button>
                </div>
                <Comments pageName="tacklexp" />
            </>
        );
    }

    return (
        <>
            <h1 className="page-title">태클 경험치 정보</h1>
            <div className="page-content">
                {Object.entries(processedData).map(([key, group]) => {
                    return (
                        <div key={key} className="tacklexp-group">
                            <h2 className="group-title">{group.name}</h2>
                            <table className="tacklexp-table">
                                <thead>
                                    <tr>
                                        <th>낚싯대</th>
                                        {group.columns.map(column => (
                                            <th key={column}>{getColumnName(column)}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.items.map((item, index) => (
                                        <tr key={item.id || index}>
                                            <td style={{ 
                                                fontWeight: 'bold',
                                                color: isDarkMode ? '#e0e0e0' : 'inherit'
                                            }}>{item.rod}</td>
                                            {group.columns.map(column => (
                                                <td key={column}>
                                                    <ColorGraph 
                                                        value={item[column]} 
                                                        groupData={group.items}
                                                        columns={group.columns}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    );
                })}
            </div>
            <Comments pageName="tacklexp" />
        </>
    );
};

export default Page1;
